import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from 'react-query'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'

import Loading from 'components/dumb/loading/Loading'
import AdxInput from 'components/custom/AdxInput'
import OnboardingContainer from 'components/smart/onboarding-container/OnboardingContainer'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import rocket_2 from 'pages/onboarding-reset-password/assets/rocket_2.png'

import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import { changePassword } from 'services/api/authentication'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = Yup.object({
  first_time: Yup.boolean().required(''),
  new_password: Yup.string().required('New Password is required'),
  new_password_confirmation: Yup.string().required('New Password Confirmation is required'),
})

const OnboardingResetPassword = () => {
  const formikRef = useRef()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleOpen = () => {
    setModalOpen(true)
  }
  const handleClose = () => {
    setModalOpen(false)
    navigate('/onboarding-personal-details')
  }

  const mutation = useMutation(changePassword, {
    onSuccess: (response) => {
      setLoading(false)
      handleOpen()
    },
    onError: (error) => {
      setLoading(false)
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        console.log(error)
      } else {
        console.log(error)
      }
    },
  })

  const apiResetPassword = async (values, action) => {
    setLoading(true)
    mutation.mutate(values)
  }

  const initialValues = {
    first_time: true,
    new_password_confirmation: '',
    new_password: '',
  }

  const PasswordEye = () => {
    return (
      <IconButton onClick={() => handleShowPassword()} sx={{ padding: 0 }}>
        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    )
  }

  return (
    <OnboardingContainer
      srcRightPhoto={rocket_2}
      bgColorRight="alternate.main"
      topRightPhoto={'145%'}
      leftRightPhoto={'115%'}
      heightRightPhoto={'125%'}
      transformRightPhoto={`translate(${-100}%, ${-100}%)`}
    >
      <Loading show={loading} />
      <Box
        height={'100%'}
        display="flex"
        justifyContent={{ xs: 'center', lg: 'flex-end' }}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
      >
        <Box
          width={{ xs: '100%', lg: '500px' }}
          height="600px"
          mr={{ lg: 7.6875 }}
          p={{ xs: 2 }}
          position={'relative'}
        >
          <Box mb={4}>
            <Typography variant="h1" mb={1}>
              Reset password
            </Typography>
            <Typography variant="body2" textAlign="justify">
              Enter a new password to secure your future logins.
            </Typography>
          </Box>
          <Box>
            <Formik
              onSubmit={apiResetPassword}
              initialValues={initialValues}
              validationSchema={validationSchema}
              innerRef={formikRef}
            >
              {({ touched, errors, values, handleChange, handleBlur, handleSubmit }) => (
                <Form>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Field
                      as={AdxInput}
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="new_password"
                      value={values.new_password}
                      placeholder="New password"
                      sx={{ width: '100%' }}
                      endAdornment={<PasswordEye />}
                      errorMessage={
                        <ErrorMessage name="new_password" component={AdxErrorMessage} />
                      }
                      isErrorShown={touched.new_password && errors.new_password}
                    />
                    <Field
                      as={AdxInput}
                      label="New Password Confirmation"
                      type={showPassword ? 'text' : 'password'}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="new_password_confirmation"
                      value={values.new_password_confirmation}
                      placeholder="* * * * * * * *"
                      sx={{ width: '100%' }}
                      endAdornment={<PasswordEye />}
                      errorMessage={
                        <ErrorMessage
                          name="new_password_confirmation"
                          component={AdxErrorMessage}
                        />
                      }
                      isErrorShown={
                        touched.new_password_confirmation && errors.new_password_confirmation
                      }
                    />
                    <Box width="100%" display="flex" alignItems="center" gap={0.75}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                      <Typography textAlign="center" variant="body1">
                        Password must be at least 8 characters
                      </Typography>
                    </Box>
                    <Box width="100%" display="flex" alignItems="center" gap={0.75}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                      <Typography textAlign="center" variant="body1">
                        Password must have at least 1 letter
                      </Typography>
                    </Box>
                    <Box width="100%" display="flex" alignItems="center" gap={0.75}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                      <Typography textAlign="center" variant="body1">
                        Password must have at least 1 number
                      </Typography>
                    </Box>
                    <AdxButton variant={'contained'} onClick={handleSubmit} sx={{ width: '100%' }}>
                      Confirm
                    </AdxButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box>

      <AdxInfoModal
        open={isModalOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={() => alert('Action button pressed')}
        muiIcon="check_circle"
        iconColor="alternate.main"
        title="Password reset"
        subtitle="Your password has been successfully reset. You may now sign in with your new password."
        footerButtons
        firstBtnLabel={'Close'}
        firstBtnHandle={handleClose}
      ></AdxInfoModal>
    </OnboardingContainer>
  )
}

export default OnboardingResetPassword
